<template>
    <kgr-generic-modal
        v-if="showDialog"
        :value="showDialog"
        :headline="tHeadline"
        :width-in-px="800"
        scrollable
        @post-input="closeDialog()"
    >
        <kgr-loading
            v-show="loading"
            inline
        />
        <div v-show="!loading" class="kgr-grid kgr-grid--1of2 kgr-grid--gutters kgr-grid--bigGutters">
            <div class="kgr-grid__cell">
                <label
                    v-for="column in tableColumnsLeft"
                    :key="column.key"
                    class="kgr-checkbox"
                >
                    <input
                        v-model="selectedColumns"
                        type="checkbox"
                        class="kgr-checkbox__control"
                        :value="column"
                        style="margin-bottom: 10px;"
                    />
                    <span class="kgr-checkbox__label">{{ $t(column.title) }}</span>
                </label>
            </div>
            <div class="kgr-grid__cell">
                <label
                    v-for="column in tableColumnsRight"
                    :key="column.key"
                    class="kgr-checkbox"
                >
                    <input
                        v-model="selectedColumns"
                        type="checkbox"
                        class="kgr-checkbox__control"
                        :value="column"
                        style="margin-bottom: 10px;"
                    />
                    <span class="kgr-checkbox__label">{{ $t(column.title) }}</span>
                </label>
            </div>
        </div>
        <template #footer>
            <kgr-button
                class="kgr-col kgr-button--outline float-left"
                @click="closeDialog"
            >
                {{ $t('cancel') }}
            </kgr-button>
            <kgr-button
                class="kgr-col"
                :pending="loading"
                @click="saveConfiguration"
            >
                {{ $t('saveAndClose') }}
            </kgr-button>
        </template>
    </kgr-generic-modal>
</template>

<script>
import GenericModal from '../../common/modal/GenericModal.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import Loading from '../../common/Loading.vue';
import Offering from '../../../js/dto/offering/Offering.js';
import TableColumn from '../../../js/dto/offering/TableColumn.js';

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-generic-modal': GenericModal,
        'kgr-loading': Loading,
    },
    props: {
        showDialog: {
            type: Boolean,
            required: true,
        },
        offering: {
            type: Offering,
            required: true,
        },
        userId: {
            type: Number,
            required: true,
        },
        tableColumns: {
            type: Array,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            loading: false,
            filteredColumns: [],
            selectedColumns: [],
        }
    },
    computed: {
        tHeadline() {
            return this.$t('offering.tableConfig');
        },
        localStorageKey() {
            let type = this.type;
            if (this.offering.isExamForDogs()) {
                type = 'dogExam.' + type;
            }
            return 'clbt.tableConfig.offering.' + type + '.' + this.userId;
        },
        tableColumnSplitIndex() {
            return Math.ceil(this.filteredColumns.length / 2);
        },
        tableColumnsLeft() {
            return this.filteredColumns.slice(0, this.tableColumnSplitIndex);
        },
        tableColumnsRight() {
            return this.filteredColumns.slice(this.tableColumnSplitIndex);
        },
    },
    methods: {
        initConfiguration() {
            if (this.loadConfiguration() === null) {
                let columns = [];
                if (this.offering.isExamForDogs()) {
                    columns.push(TableColumn.DOG);
                }
                if (this.type === TableColumn.TABLE_TYPE_PARTICIPANTS) {
                    columns.push(
                        TableColumn.NAME,
                        TableColumn.CONTRACT_STATUS,
                        TableColumn.AGE,
                        TableColumn.EMAIL,
                        TableColumn.PHONE,
                        TableColumn.PHONE_MOBILE,
                        TableColumn.EMERGENCY_CONTACT,
                        TableColumn.BOOKED_AT,
                        TableColumn.ATTENDEE_STATUS,
                        TableColumn.COMMENT,
                    );
                } else if (this.type === TableColumn.TABLE_TYPE_CANCELLATIONS) {
                    columns.push(
                        TableColumn.NAME,
                        TableColumn.CONTRACT_STATUS,
                        TableColumn.AGE,
                        TableColumn.EMAIL,
                        TableColumn.PHONE,
                        TableColumn.PHONE_MOBILE,
                        TableColumn.EMERGENCY_CONTACT,
                        TableColumn.BOOKED_AT,
                        TableColumn.CANCELED_AT,
                        TableColumn.ATTENDEE_STATUS,
                        TableColumn.COMMENT,
                    );
                } else if (this.type === TableColumn.TABLE_TYPE_WAITLIST) {
                    columns.push(
                        TableColumn.NAME,
                        TableColumn.CONTRACT_STATUS,
                        TableColumn.AGE,
                        TableColumn.EMAIL,
                        TableColumn.PHONE,
                        TableColumn.PHONE_MOBILE,
                        TableColumn.EMERGENCY_CONTACT,
                        TableColumn.BOOKED_AT,
                        TableColumn.INFORMED_AT,
                        TableColumn.STATUS_WAITLIST,
                        TableColumn.COMMENT,
                    );
                } else if ([TableColumn.TABLE_TYPE_ACCEPTED, TableColumn.TABLE_TYPE_REJECTED, TableColumn.TABLE_TYPE_UNSURE].includes(this.type)) {
                    columns.push(
                        TableColumn.NAME,
                        TableColumn.CONTRACT_STATUS,
                        TableColumn.AGE,
                        TableColumn.EMAIL,
                        TableColumn.PHONE,
                        TableColumn.PHONE_MOBILE,
                        TableColumn.EMERGENCY_CONTACT,
                        TableColumn.REPLIED_AT,
                        TableColumn.ATTENDEE_STATUS,
                        TableColumn.COMMENT,
                    );
                } else if (this.type === TableColumn.TABLE_TYPE_ATTENDANCE) {
                    window.localStorage.setItem(this.localStorageKey, JSON.stringify([]));
                    return;
                }
                if (columns.length > 0) {
                    window.localStorage.setItem(this.localStorageKey, JSON.stringify(columns));
                }
            }
        },
        initColumns() {
            this.filteredColumns = this.filterColumns();
            const selected = this.loadConfiguration();
            if (selected === null) {
                this.selectedColumns = this.filteredColumns;
                return;
            }
            this.filteredColumns.forEach(column => {
                if (selected.includes(column.key)) {
                    this.selectedColumns.push(column);
                }
            });
        },
        filterColumns() {
            if (this.offering.useWaitList) {
                if (this.type === TableColumn.TABLE_TYPE_PARTICIPANTS || this.type === TableColumn.TABLE_TYPE_CANCELLATIONS) {
                    return this.tableColumns.filter(column => !TableColumn.WAITLIST_COLUMNS.includes(column.key));
                }
                if (this.type === TableColumn.TABLE_TYPE_WAITLIST) {
                    return this.tableColumns.filter(column => column.key !== TableColumn.CANCELED_AT);
                }
            }
            return this.tableColumns;
        },
        saveConfiguration() {
            const selectedColumns = this.selectedColumns.map(column => column.key);
            window.localStorage.setItem(this.localStorageKey, JSON.stringify(selectedColumns));
            this.closeDialog();
            this.$emit('change', selectedColumns)
        },
        loadConfiguration() {
            return JSON.parse(window.localStorage.getItem(this.localStorageKey));
        },
        closeDialog() {
            this.$emit('closeDialog');
        },
    },
    mounted() {
        this.initConfiguration();
        this.initColumns();
    },
}
</script>

<style scoped lang="scss">
.float-left {
    float: left;
}
</style>
